import React, { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";
import LeadChat from "../common/LeadChat";
import Card from "../common/Card";
import { useUnreadLeads } from "../../hooks/UnreadLeadsContext";

function fmtRelTime(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

export default function CustomerMessages({ session, onBack }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  // Key is `${leadId}-${businessId}` — one open slot across all cards
  const [openKey, setOpenKey] = useState(null);
  const [readKeys, setReadKeys] = useState(() => new Set());
  const { markLeadRead } = useUnreadLeads();


  useEffect(() => {
    if (!session?.user?.id) { setLoading(false); return; }
    loadThreads();
  // eslint-disable-next-line -- intentional: loadThreads defined in component, stable for this effect
  }, [session?.user?.id]);

  async function loadThreads() {
    setLoading(true);
    try {
      // Fetch leads linked to this user's account
      const { data: accountLeads } = await supabase
        .from("leads")
        .select("id, customer_name, trade, project_type, suburb")
        .eq("customer_user_id", session.user.id)
        .order("created_at", { ascending: false });

      // Also fetch guest leads matched by email
      const userEmail = session.user.email;
      let guestLeads = [];
      if (userEmail) {
        const { data } = await supabase
          .from("leads")
          .select("id, customer_name, trade, project_type, suburb")
          .is("customer_user_id", null)
          .eq("customer_email", userEmail);
        guestLeads = data || [];
      }

      // Merge and deduplicate
      const seen = new Set();
      const leadsData = [...(accountLeads || []), ...guestLeads].filter((l) => {
        if (seen.has(l.id)) return false;
        seen.add(l.id);
        return true;
      });

      if (!leadsData || leadsData.length === 0) {
        setThreads([]);
        return;
      }

      const leadIds = leadsData.map((l) => l.id);
      const leadMap = Object.fromEntries(leadsData.map((l) => [l.id, l]));

      // Find ALL conversations by looking at lead_messages directly
      // Each unique (lead_id, business_id) pair where business_id is not null = a thread
      const { data: msgThreads } = await supabase
        .from("lead_messages")
        .select("lead_id, business_id")
        .in("lead_id", leadIds)
        .not("business_id", "is", null);

      // Deduplicate to unique (lead_id, business_id) pairs
      const seenPairs = new Set();
      const pairs = (msgThreads || []).filter(({ lead_id, business_id }) => {
        const k = `${lead_id}-${business_id}`;
        if (seenPairs.has(k)) return false;
        seenPairs.add(k);
        return true;
      });

      // Also include threads from quotes (tradie may have quoted but not messaged)
      const { data: quotesData } = await supabase
        .from("quotes")
        .select("lead_id, business_id")
        .in("lead_id", leadIds);

      (quotesData || []).forEach(({ lead_id, business_id }) => {
        if (!business_id) return;
        const k = `${lead_id}-${business_id}`;
        if (seenPairs.has(k)) return;
        seenPairs.add(k);
        pairs.push({ lead_id, business_id });
      });

      // Fetch business names
      const bizIds = [...new Set(pairs.map((p) => p.business_id).filter(Boolean))];
      let bizMap = {};
      if (bizIds.length > 0) {
        const { data: bizData } = await supabase
          .from("businesses")
          .select("id, name")
          .in("id", bizIds);
        (bizData || []).forEach((b) => { bizMap[b.id] = b.name; });
      }

      // Fetch last message + count for each thread
      const threadData = await Promise.all(
        pairs.map(async ({ lead_id, business_id }) => {
          const { data, count } = await supabase
            .from("lead_messages")
            .select("id, body, sender_role, sender_name, created_at", { count: "exact" })
            .eq("lead_id", lead_id)
            .or(`business_id.eq.${business_id},sender_role.eq.customer`)
            .order("created_at", { ascending: false })
            .limit(1);
          return {
            lead: leadMap[lead_id],
            businessName: bizMap[business_id] || "Tradie",
            businessId: business_id,
            lastMsg: data?.[0] || null,
            msgCount: count || 0,
          };
        })
      );

      // Show threads with messages, sorted by most recent
      const active = threadData
        .filter((t) => t.msgCount > 0)
        .sort((a, b) => {
          const ta = a.lastMsg?.created_at ? new Date(a.lastMsg.created_at).getTime() : 0;
          const tb = b.lastMsg?.created_at ? new Date(b.lastMsg.created_at).getTime() : 0;
          return tb - ta;
        });

      setThreads(active);
    } finally {
      setLoading(false);
    }
  }

  function markRead(key) {
    setReadKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }

  if (loading) {
    return (
      <Card>
        <p className="muted" style={{ textAlign: "center", padding: "24px 0" }}>Loading messages…</p>
      </Card>
    );
  }

  return (
    <div className="page customer-shell">
      {/* Back button */}
      <button
        type="button"
        onClick={onBack}
        style={{
          background: "none",
          border: "none",
          color: "var(--text-muted, #6b7280)",
          fontSize: "0.9rem",
          cursor: "pointer",
          padding: "0 0 16px",
          display: "flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        ← Back to dashboard
      </button>

      <h2 style={{ margin: "0 0 16px", fontWeight: 700, fontSize: "1.25rem" }}>
        💬 Messages
      </h2>

      {threads.length === 0 ? (
        <Card>
          <p className="muted">
            No messages yet. Once a tradie sends you a message, it will appear here.
          </p>
        </Card>
      ) : (
        threads.map(({ lead, businessName, businessId, lastMsg, msgCount }) => {
          const threadKey = `${lead.id}-${businessId}`;
          const isOpen = openKey === threadKey;
          const isRead = readKeys.has(threadKey);

          return (
            <div
              key={threadKey}
              style={{
                marginBottom: "12px",
                border: "1px solid var(--border, #e5e7eb)",
                borderRadius: "14px",
                overflow: "hidden",
                background: "var(--surface, #fff)",
              }}
            >
              {/* Thread header row */}
              <div
                role="button"
                tabIndex={0}
                style={{ padding: "14px 16px", cursor: "pointer", display: "flex", alignItems: "flex-start", gap: "12px" }}
                onClick={() => {
                  const opening = openKey !== threadKey;
                  setOpenKey(opening ? threadKey : null);
                  if (opening) {
                    markRead(threadKey);
                    markLeadRead(lead.id);
                  }
                }}
                onKeyDown={(e) => e.key === "Enter" && (() => {
                  const opening = openKey !== threadKey;
                  setOpenKey(opening ? threadKey : null);
                  if (opening) {
                    markRead(threadKey);
                    markLeadRead(lead.id);
                  }
                })()}
              >
                {/* Unread dot */}
                <div style={{ paddingTop: "4px", flexShrink: 0 }}>
                  <div style={{
                    width: "9px",
                    height: "9px",
                    borderRadius: "50%",
                    background: isRead ? "transparent" : "var(--primary)",
                    border: isRead ? "1.5px solid var(--border, #e5e7eb)" : "none",
                    transition: "background 0.2s, border 0.2s",
                  }} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "var(--text, #1C1C1E)", marginBottom: "2px" }}>
                    {[lead.trade, lead.project_type, lead.suburb].filter(Boolean).join(" · ")}
                  </div>
                  {businessName && (
                    <div style={{ fontSize: "0.8rem", color: "var(--text-muted, #6b7280)", marginBottom: "4px" }}>
                      {businessName}
                    </div>
                  )}
                  {lastMsg && (
                    <div style={{ fontSize: "0.85rem", color: "var(--text-muted, #6b7280)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <span style={{ fontWeight: lastMsg.sender_role === "tradie" ? 600 : 400 }}>
                        {lastMsg.sender_role === "tradie" ? (businessName || "Tradie") : "You"}:
                      </span>
                      {" "}
                      {(lastMsg.body || "📎 Attachment").slice(0, 80)}
                    </div>
                  )}
                </div>

                <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px" }}>
                  <span style={{ fontSize: "0.75rem", color: "var(--text-muted, #6b7280)", whiteSpace: "nowrap" }}>
                    {fmtRelTime(lastMsg?.created_at)}
                  </span>
                  <span style={{ fontSize: "0.72rem", color: "var(--text-muted, #6b7280)" }}>
                    {msgCount} msg{msgCount !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>

              {/* Expanded chat */}
              {isOpen && (
                <div style={{ borderTop: "1px solid var(--border, #e5e7eb)" }}>
                  <LeadChat
                    leadId={lead.id}
                    senderRole="customer"
                    senderName={(lead.customer_name || "Customer").split(" ")[0]}
                    businessId={businessId}
                  />
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
